import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { DisclosureNext, DisclosurePrevious } from '@sas/react-svg';
import { useMounted } from '../../../hooks/useMounted';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { useFormatLocale } from '../../../locale/formatting/useFormatLocale';
import { isArSaLocale, isThaiLocale } from '../../../locale/localeUtils';
import { Button, ButtonVariant } from '../../Button';
import { useDefaultDateTimeFormat } from '../../DatePicker';
import { convertGregorianYearToThaiYear, convertThaiYearToGregorianYear, getMonthAndYearByDate, getMonthsForLocale, getYearOrder } from '../../DatePicker/DateUtil';
import { Input, useNumericValidationAdapter, validateNumericInput } from '../../Input';
import { Select } from '../../Select';
import bundle from '../NovaComponents-Calendar-gui-icu.properties';
import cn from '../Calendar.css.json';
import '../Calendar.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
export const MonthCalendarHeader = /*#__PURE__*/forwardRef(function MonthCalendarHeader(props, ref) {
  const dayFormat = useDefaultDateTimeFormat({
    type: 'dayOnly'
  });
  const monthFormat = useDefaultDateTimeFormat({
    type: 'monthOnly'
  });
  const yearFormat = useDefaultDateTimeFormat({
    type: 'yearOnly'
  });
  const minDate = useMemo(() => props.minDate ?? new Date(1582, 0, 1), [props.minDate]);
  const maxDate = useMemo(() => props.maxDate ?? new Date(9999, 11, 31), [props.maxDate]);
  const localize = useTextLocalizer();
  const locale = useFormatLocale();
  const minMonthAllowed = minDate.getMonth();
  const maxMonthAllowed = maxDate.getMonth();
  const minMonthFormated = parseInt(monthFormat(minDate));
  const maxMonthFormated = parseInt(monthFormat(maxDate));
  const formatedMinYear = parseInt(yearFormat(minDate));
  const formatedMaxYear = parseInt(yearFormat(maxDate));
  const minYearAllowed = isArSaLocale(locale) ? formatedMinYear : minDate.getFullYear();
  const maxYearAllowed = isArSaLocale(locale) ? formatedMaxYear : maxDate.getFullYear();
  const yearOrder = useMemo(() => getYearOrder(locale), [locale]);
  const months = useMemo(() => getMonthsForLocale(locale), [locale]);
  const showPreviousButton = props.showPreviousButton ?? true;
  const showNextButton = props.showNextButton ?? true;
  const [yearValue, setYearValue] = useState(String(props.year));
  const [yearValue2, setYearValue2] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(props.formatedMonth);
  const yearValueFormated = useMemo(() => {
    const year = yearValue2 ?? yearFormat(new Date(props.year, props.month, props.focusDate));
    return year;
  }, [props.month, props.focusDate, yearFormat, yearValue2, props.year]);
  const monthValueFormated = parseInt(monthFormat(new Date(props.year, props.month, props.focusDate)));
  const [isValid, setIsValid] = useState(true);
  const [isMonthValid, setIsMonthValid] = useState(true);
  const mountedRef = useMounted();
  const validateYearInput = useMemo(() => (value, appLocale, isRequired, allowIntegersOnly, isLive, minLength) => {
    const result = validateNumericInput(value, appLocale, isRequired, allowIntegersOnly, isLive, isArSaLocale(locale) ? 3 : minLength);
    const maxLength = isThaiLocale(locale) ? 5 : 4;
    const minValidationYear = isThaiLocale(locale) ? convertGregorianYearToThaiYear(minYearAllowed).toString() : isArSaLocale(locale) ? formatedMinYear : minYearAllowed.toString();
    const maxValidationYear = isThaiLocale(locale) ? convertGregorianYearToThaiYear(maxYearAllowed).toString() : isArSaLocale(locale) ? formatedMaxYear : maxYearAllowed.toString();
    if (result.isError) {
      result.message = localize('minMaxYear.error.msg', {
        min: minValidationYear.toString(),
        max: maxValidationYear.toString()
      });
      if (mountedRef.current) setIsValid(false);
    } else {
      let yearValue = parseInt(value);
      if (isThaiLocale(locale)) yearValue = convertThaiYearToGregorianYear(yearValue);
      if (value.includes(' ') || !isLive && (yearValue < minYearAllowed || yearValue > maxYearAllowed) || isLive && (yearValue.toString().length === maxLength || value.length === maxLength) && (yearValue < minYearAllowed || yearValue > maxYearAllowed)) {
        result.isError = true;
        result.message = localize('minMaxYear.error.msg', {
          min: minValidationYear.toString(),
          max: maxValidationYear.toString()
        });
        if (mountedRef.current) setIsValid(false);
      } else if (mountedRef.current && value.length > 3 || isArSaLocale(locale) && value.length >= 3) {
        setIsValid(true);
      }
    }
    if (mountedRef.current) setYearValue(value);
    return result;
  }, [locale, localize, maxYearAllowed, minYearAllowed, mountedRef, formatedMinYear, formatedMaxYear]);
  const integerValidator = useNumericValidationAdapter({
    validateInputFunction: validateYearInput,
    isRequired: true,
    allowIntegersOnly: true,
    minLength: isArSaLocale(locale) ? 3 : 4,
    initialValue: String(props.year),
    disableAutoFormatting: true
  });
  const validateArSalocaleMonth = useCallback((value, year) => {
    const formattedMonth = parseInt(monthFormat(new Date(year, value, props.focusDate)));
    const formattedYear = parseInt(yearFormat(new Date(year, value, props.focusDate)));
    if (formattedYear === formatedMinYear && formattedMonth < minMonthFormated) {
      setIsMonthValid(false);
      return;
    } else if (formattedYear === formatedMaxYear && formattedMonth > maxMonthFormated) {
      setIsMonthValid(false);
      return;
    } else {
      return setIsMonthValid(true);
    }
  }, [monthFormat, yearFormat, props.focusDate, formatedMaxYear, formatedMinYear, maxMonthFormated, minMonthFormated]);
  const validateMonthValue = useCallback((value, year) => {
    const date = new Date(year, value, 5);
    const testMinDate = new Date(minDate);
    testMinDate.setDate(1);
    if (isThaiLocale(locale)) testMinDate.setFullYear(convertGregorianYearToThaiYear(testMinDate.getFullYear()));
    const testMaxDate = new Date(maxDate);
    testMaxDate.setDate(27); // All months have day 27
    if (isThaiLocale(locale)) testMaxDate.setFullYear(convertGregorianYearToThaiYear(testMaxDate.getFullYear()));
    if (date.getTime() < testMinDate.getTime() || date.getTime() > testMaxDate.getTime()) setIsMonthValid(false);else setIsMonthValid(true);
  }, [locale, minDate, maxDate]);
  const handleChange = e => {
    setYearValue2(e.value);
    if (!mountedRef.current) return;
    integerValidator.handleChange(e);
    if (e.value.length >= 4 || isArSaLocale(locale) && e.value.length >= 3) {
      const result = validateYearInput(e.value, locale, true, true, false, isArSaLocale(locale) ? 3 : 4);
      if (!result.isError) {
        if (isArSaLocale(locale)) {
          const monthIndex = monthValueFormated - 1;
          const {
            year,
            month,
            day
          } = convertDateToGregorian(props.focusDate, props.month, props.year, parseInt(e.value), monthIndex, monthIndex);
          props.onChange({
            month,
            year,
            day
          });
          props.onYearChange(getDisplayedDate(month, year));
          validateArSalocaleMonth(month, year);
        } else validateMonthValue(props.month, parseInt(e.value));
      }
    }
  };
  const handleBlur = () => {
    if (!mountedRef.current) return;
    const year = isArSaLocale(locale) ? yearValueFormated : yearValue;
    if (!isNaN(parseInt(year)) || year.trim() === '') {
      const result = validateYearInput(year, locale, true, true, false, isArSaLocale(locale) ? 3 : 4);
      if (result.isError) {
        integerValidator.handleBlur(); // Show error in control
        return;
      }
    }
  };
  const reset = useCallback(() => {
    if (!isArSaLocale(locale) && !props.dayCellPress) {
      setYearValue(String(props.year));
      validateYearInput(String(props.year), locale, true, true, false, 4);
      validateMonthValue(props.month, props.year);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, props.month, props.year, validateMonthValue, validateYearInput]);
  useEffect(() => {
    reset();
  }, [reset]);
  useEffect(() => {
    if (isArSaLocale(locale)) {
      validateArSalocaleMonth(props.month, props.year);
    }
  }, [validateArSalocaleMonth, props.month, props.year, locale]);
  useEffect(() => {
    // need to prevent the month header from showing the same month twice
    if (props.numberMonthsDisplayed > 1) {
      setSelectedIndex(props.formatedMonth);
    }
  }, [props.formatedMonth, selectedIndex, props.displayedMonths, props.numberMonthsDisplayed]);
  useEffect(() => {
    // changing the year in one month also updates the year for the other months
    // and we need to make sure integerValidator is updated for those months.
    if (props.numberMonthsDisplayed > 1) {
      handleChange({
        value: yearValue
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearValue]);
  const refSelect = useRef(null);
  const refInput = useRef(null);
  useImperativeHandle(ref, () => ({
    isValid: () => {
      if (!isValid || !isMonthValid) return false;
      return true;
    },
    focus: () => !isMonthValid ? refSelect.current?.focus() : refInput.current?.focus(),
    reset
  }));
  const minMaxMonthErrorMsg = localize('minMaxMonth.error.msg', {
    min: getMonthAndYearByDate(locale, minDate),
    max: getMonthAndYearByDate(locale, maxDate)
  });
  const getDisplayedDate = (month, year) => {
    const lastday = new Date(year, month + 1, 0).getDate();
    return new Date(year, month, props.focusDate > lastday ? lastday : props.focusDate);
  };
  const convertDateToGregorian = useCallback((day, month, year, localeYear, currentMonthIndex, selectedMonthIndex) => {
    let currentYear = localeYear;
    let currentMonth = selectedMonthIndex;
    let currentDay = day;
    let count = 0;
    let newDate = new Date();
    let formatedYear = parseInt(yearFormat(newDate));
    let yearDifference = formatedYear - newDate.getFullYear();
    if (!isArSaLocale(locale) || yearDifference === 0) return {
      day,
      month,
      year: localeYear
    };
    while (yearDifference !== 0 && count < 5) {
      count++;
      currentYear -= yearDifference;
      newDate = new Date(currentYear, currentMonth, currentDay);
      formatedYear = parseInt(yearFormat(newDate));
      yearDifference = formatedYear - localeYear;
    }
    count = 0;
    while (yearDifference !== 0 && count < 12) {
      count++;
      if (yearDifference < 0) {
        currentMonth++;
      } else if (yearDifference > 0) {
        currentMonth--;
      }
      newDate = new Date(currentYear, currentMonth, currentDay);
      formatedYear = parseInt(yearFormat(newDate));
      yearDifference = formatedYear - localeYear;
    }
    if (typeof currentMonthIndex === 'number' && yearDifference === 0) {
      const selectedFormatedMonth = currentMonthIndex + 1;
      let formatedMonth = parseInt(monthFormat(new Date(currentYear, currentMonth, currentDay)));
      let monthDifference = formatedMonth - selectedFormatedMonth;
      count = 0;
      while (monthDifference !== 0 && yearDifference === 0 && count < 12) {
        count++;
        if (monthDifference > 0) {
          currentMonth--;
        } else if (monthDifference < 0) {
          currentMonth++;
        }
        newDate = new Date(currentYear, currentMonth, currentDay);
        formatedYear = parseInt(yearFormat(newDate));
        yearDifference = formatedYear - localeYear;
        if (yearDifference > 0) {
          currentMonth--;
          break;
        } else if (yearDifference < 0) {
          currentMonth++;
          break;
        } else {
          formatedMonth = parseInt(monthFormat(new Date(currentYear, currentMonth, currentDay)));
          monthDifference = formatedMonth - selectedFormatedMonth;
        }
      }
      formatedMonth = parseInt(monthFormat(new Date(currentYear, currentMonth, currentDay)));
      monthDifference = formatedMonth - selectedFormatedMonth;
      count = 0;
      while (monthDifference !== 0 && count < 31) {
        count++;
        if (monthDifference > 0) {
          currentDay--;
        } else if (monthDifference < 0) {
          currentDay++;
        }
        newDate = new Date(currentYear, currentMonth, currentDay);
        formatedMonth = parseInt(monthFormat(new Date(currentYear, currentMonth, currentDay)));
        monthDifference = formatedMonth - selectedFormatedMonth;
      }
    }
    const dayDifference = parseInt(dayFormat(new Date(currentYear, currentMonth, currentDay))) - parseInt(dayFormat(new Date(year, month, day)));
    if (dayDifference !== 0) currentDay -= dayDifference;
    const newDateCreated = new Date(currentYear, currentMonth, currentDay);
    return {
      year: newDateCreated.getFullYear(),
      month: newDateCreated.getMonth(),
      day: newDateCreated.getDate()
    };
  }, [yearFormat, monthFormat, locale, dayFormat]);
  const firstDisplayedMonth = () => {
    const firstMonth = props.displayedMonths?.[0];
    if (firstMonth === undefined) return 0;
    return firstMonth;
  };
  const lastDisplayedMonth = () => {
    const lastMonth = props.displayedMonths?.[props.displayedMonths?.length - 1];
    if (lastMonth === undefined) return 0;
    return lastMonth;
  };
  const yearElement = /*#__PURE__*/_jsx(Input, {
    ref: refInput,
    testId: "calendarHeaderYearInput",
    className: cn.headerYearInput,
    value: isArSaLocale(locale) ? yearValueFormated : yearValue,
    onCommit: e => {
      if (!mountedRef.current) return;
      if (e.value === '') return;
      if (e.reason === 'blur' && props.year === parseInt(e.value)) return;
      if (e.reason === 'enter') {
        const result = validateYearInput(e.value, locale, true, true, false, isArSaLocale(locale) ? 3 : 4);
        if (result.isError) {
          integerValidator.handleBlur(); // Show error in control
          return;
        }
      }
      if (isValid) {
        if (isArSaLocale(locale)) {
          const monthIndex = monthValueFormated - 1;
          const {
            year,
            month,
            day
          } = convertDateToGregorian(props.focusDate, props.month, props.year, parseInt(e.value), monthIndex, monthIndex);
          props.onChange({
            month,
            year,
            day
          });
          props.onYearChange(getDisplayedDate(month, year));
          setYearValue2(null);
        } else {
          props.onChange({
            month: props.month,
            year: parseInt(e.value)
          });
          props.onYearChange(getDisplayedDate(props.month, parseInt(e.value)));
        }
      }
    },
    onFocus: () => {
      if (!mountedRef.current) return;
      const year = isArSaLocale(locale) ? yearValueFormated : yearValue;
      if (!isNaN(parseInt(year)) || year.trim() === '') {
        const result = validateYearInput(year, locale, true, true, false, isArSaLocale(locale) ? 3 : 4);
        if (result.isError) {
          integerValidator.handleBlur(); // Show error in control
          return;
        }
      }
    },
    onChange: handleChange,
    onBlur: handleBlur,
    status: isValid ? undefined : 'alert',
    message: integerValidator.errors.message,
    maxLength: isThaiLocale(locale) ? 5 : 4,
    ariaLabel: localize('year.aria'),
    required: true
  });
  return /*#__PURE__*/_jsxs("div", {
    className: cn.header,
    children: [/*#__PURE__*/_jsx(Button, {
      icon: {
        icon: DisclosurePrevious,
        tooltip: localize('previousMonth.aria')
      },
      variant: ButtonVariant.Icon,
      focusable: false,
      onPress: () => {
        const month = props.month === 0 ? 11 - (props.numberMonthsDisplayed - 1) : props.month - props.numberMonthsDisplayed;
        const year = props.month === 0 ? props.year - 1 : props.year;
        props.onChange({
          month,
          year
        });
        props.onPreviousButtonPress?.(getDisplayedDate(month, year));
        if (props.month === 0) setYearValue(String(props.year - 1));
      },
      testId: "calendarHeaderPreviousButton",
      disabled: !isValid || (isArSaLocale(locale) ? parseInt(yearValueFormated) === formatedMinYear && (monthValueFormated <= minMonthFormated || props.numberMonthsDisplayed > 1 && parseInt(monthFormat(new Date(props.year, firstDisplayedMonth(), 1))) <= minMonthFormated) : (props.month <= minMonthAllowed || props.numberMonthsDisplayed > 1 && firstDisplayedMonth() <= minMonthAllowed) && props.year === (isThaiLocale(locale) ? convertGregorianYearToThaiYear(minYearAllowed) : minYearAllowed)),
      style: {
        visibility: showPreviousButton === false ? 'hidden' : undefined
      }
    }), /*#__PURE__*/_jsxs("div", {
      className: cn.headerMonthYearContainer,
      children: [yearOrder === 'before' && yearElement, /*#__PURE__*/_jsx(Select, {
        testId: "calendarHeaderMonthSelect",
        selectBoxRef: refSelect,
        className: cn.headerMonthButton,
        popoverClassName: props.selectClassName,
        selectedIndex: props.numberMonthsDisplayed > 1 ? selectedIndex : props.formatedMonth,
        values: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(monthIndex => ({
          text: months[monthIndex]
        })),
        onSelected: value => {
          if (typeof value === 'number' && !isNaN(parseInt(yearValue)) && isValid) {
            setSelectedIndex(value);
            if (!isArSaLocale(locale)) {
              props.onChange({
                month: value,
                year: parseInt(yearValue)
              });
              props.onMonthSelect(getDisplayedDate(value, props.year));
              validateMonthValue(value, parseInt(yearValue));
              return;
            }
            const monthDifference = props.formatedMonth - value;
            let currentMonth = monthDifference > 0 ? props.month - monthDifference : props.month + Math.abs(monthDifference);
            if (currentMonth < 0) {
              currentMonth = 12 - Math.abs(currentMonth);
            }
            if (currentMonth >= 12) {
              currentMonth = currentMonth - 12;
            }
            const {
              year,
              month,
              day
            } = convertDateToGregorian(props.focusDate, props.month, props.year, parseInt(yearValueFormated), value, currentMonth);
            props.onChange({
              month,
              year,
              day
            });
            props.onMonthSelect(getDisplayedDate(month, year));
            validateArSalocaleMonth(month, year);
          }
        },
        ariaLabel: localize('month.aria'),
        message: isValid && !isMonthValid ? minMaxMonthErrorMsg : undefined,
        status: isValid && !isMonthValid ? 'alert' : undefined,
        disabled: !isValid
      }), yearOrder === 'after' && yearElement]
    }), /*#__PURE__*/_jsx(Button, {
      icon: {
        icon: DisclosureNext,
        tooltip: localize('nextMonth.aria')
      },
      variant: ButtonVariant.Icon,
      focusable: false,
      onPress: () => {
        const month = props.month === 11 ? 0 : props.month + props.numberMonthsDisplayed;
        const year = props.month === 11 ? props.year + 1 : props.year;
        props.onChange({
          month,
          year
        });
        props.onNextButtonPress?.(getDisplayedDate(month, year));
        if (props.month === 11) setYearValue(String(props.year + 1));
      },
      testId: "calendarHeaderNextButton",
      disabled: !isValid || (isArSaLocale(locale) ? parseInt(yearValueFormated) === formatedMaxYear && (monthValueFormated >= maxMonthFormated || props.numberMonthsDisplayed > 1 && parseInt(monthFormat(new Date(props.year, lastDisplayedMonth(), 1))) >= maxMonthFormated) : (props.month >= maxMonthAllowed || props.numberMonthsDisplayed > 1 && lastDisplayedMonth() >= maxMonthAllowed) && props.year === (isThaiLocale(locale) ? convertGregorianYearToThaiYear(maxYearAllowed) : maxYearAllowed)),
      style: {
        visibility: showNextButton === false ? 'hidden' : undefined
      }
    })]
  });
});
//# sourceMappingURL=MonthCalendarHeader.js.map